<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head kt-portlet__head--lg">
      <div class="kt-portlet__head-label">
        <span class="kt-portlet__head-icon">
          <i class="fas fa-file-alt"></i>
        </span>
        <h3 class="kt-portlet__head-title">
          ตรวจสอบข้อมูลก่อนบันทึก
        </h3>
      </div>
    </div>
    <div class="kt-portlet__body">
      <!-- begin: Stepper -->
      <div class="row">
        <div class="col-12">
          <div class="fss-stepper">
            <div class="fss-step fss-step--done">
              <div class="fss-step__badge">&#10003;</div>
              <div class="fss-step__title">เลือก Filter</div>
            </div>
            <div class="fss-connector fss-connector--done"></div>
            <div class="fss-step fss-step--active">
              <div class="fss-step__badge">2</div>
              <div class="fss-step__title">ตรวจสอบข้อมูล</div>
            </div>
            <div class="fss-connector"></div>
            <div class="fss-step">
              <div class="fss-step__badge">3</div>
              <div class="fss-step__title">บันทึก + พิมพ์</div>
            </div>
          </div>
        </div>
      </div>
      <!-- end: Stepper -->

      <!-- begin: Warning banner -->
      <div class="row">
        <div class="col-12">
          <div class="alert alert-warning" role="alert">
            <strong>ยังไม่บันทึก</strong> —
            ตรวจสอบข้อมูลแล้วกด "บันทึกข้อมูล" เพื่อล็อค snapshot + ออกเลขที่เอกสาร
          </div>
        </div>
      </div>
      <!-- end: Warning banner -->

      <!-- begin: Filter ที่เลือก -->
      <div class="row">
        <div class="col-12">
          <h5 class="kt-font-bold kt-margin-b-10">Filter ที่เลือก</h5>
          <div v-if="filterText" class="fss-filter-text">
            {{ filterText }}
            <!-- FS-108 — บรรทัดสาขาย้ายไปอยู่ใต้ชื่อในตารางระดับตัวแทนแล้ว (ผู้ใช้สั่ง 15/10/2026) -->
          </div>
          <table v-else-if="filterLabels.length > 0" class="table table-sm fss-filter-table">
            <tbody>
              <tr v-for="(item, index) in filterLabels" :key="index">
                <td class="fss-filter-table__label">{{ item.label }}</td>
                <td>
                  {{ item.text }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-muted">-</div>
        </div>
      </div>
      <!-- end: Filter ที่เลือก -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: ไม่พบข้อมูล -->
      <!--
        กรณีรหัสที่เลือกไม่ใช่สำนักงานใหญ่ (status จาก API) หรือไม่มีบาร์โค้ดคงค้างตามเงื่อนไข
        → แสดงข้อความไทยแทนตารางเปล่า ตาม CR
      -->
      <div v-if="isNoData" class="row">
        <div class="col-12">
          <div class="alert alert-secondary text-center" role="alert">
            <div class="kt-font-bold">ไม่พบข้อมูล</div>
            <div class="text-muted">{{ noDataMessage }}</div>
          </div>
        </div>
      </div>
      <!-- end: ไม่พบข้อมูล -->

      <!-- begin: Summary table -->
      <!--
        FS-108 (15/10/2026) — เลือก 1 ราย (ตัวแทนจำหน่าย หรือ Group) ต้องแสดงแบบเดียวกับโหมดหลายตัวแทน
          ตารางระดับตัวแทน (ลำดับ · ชื่อตัวแทนจำหน่าย · จำนวนคงค้าง · ยอดรวม) + รายละเอียดสินค้าใต้แถว
          • ไม่มีช่องค้นหา (มีแค่ 1 ราย) · ไม่มีปุ่มลูกศร — รายละเอียดกางไว้ตลอด (ผู้ใช้ตอบ 15/10/2026)
          • Group → บรรทัดสาขาใต้ชื่อ (FS-106) · ตัวแทนจำหน่าย → ไม่มี (FS-107)
          • หัวรายละเอียดแสดงชื่ออย่างเดียว ไม่มี [รหัส] (FS-106 ข้อ 2 / FS-107)
          ตัวเลขมาจาก processResult ชุดเดิม — จัดวางใหม่อย่างเดียว ไม่ได้คำนวณใหม่
        คลาส/หน้าตาเดียวกับ FormSummaryStockGroupPreview.vue
      -->
      <div v-else>
        <div class="row">
          <div class="col-lg-12 table-responsive">
            <table
              class="table table-sm table-bordered fss-agent-table"
              data-test="fss-preview-agent-table"
            >
              <thead>
                <tr>
                  <th class="text-center" style="width: 8%;">ลำดับ</th>
                  <th class="text-center">ชื่อตัวแทนจำหน่าย</th>
                  <th class="text-center" style="width: 16%;">จำนวนคงค้าง</th>
                  <th class="text-center" style="width: 22%;">ยอดรวม (ก่อน VAT)</th>
                </tr>
              </thead>
              <tbody>
                <tr data-test="fss-preview-agent-row">
                  <td class="text-center">1</td>
                  <td>
                    {{ agentLabel }}
                    <div
                      v-if="branchLine"
                      class="fss-branch-line"
                      data-test="fss-preview-branch-line"
                    >
                      {{ branchLine }}
                    </div>
                  </td>
                  <td class="text-right">{{ formatQty(totalQty) }}</td>
                  <td class="text-right">{{ formatAmount(totalAmount) }}</td>
                </tr>

                <!-- แถวรายละเอียดระดับสินค้า — กางไว้ตลอด (FS-108) -->
                <tr class="fss-detail-row" data-test="fss-preview-detail-row">
                  <td colspan="4">
                    <div class="fss-detail">
                      <div class="fss-detail__title" data-test="fss-preview-detail-title">
                        รายละเอียดสินค้าคงค้าง — {{ agentNameOnly }}
                      </div>
                      <div class="table-responsive">
                        <table class="table table-sm table-striped table-bordered">
                          <thead>
                            <tr>
                              <th class="text-center" style="width: 7%;">ลำดับ</th>
                              <th class="text-center" style="width: 18%;">ประเภทสินค้า</th>
                              <th class="text-center" style="width: 15%;">รหัสสินค้า</th>
                              <th class="text-center" style="width: 27%;">ชื่อสินค้า</th>
                              <th class="text-center" style="width: 12%;">จำนวนคงค้าง</th>
                              <th class="text-center" style="width: 8%;">หน่วย</th>
                              <th class="text-center" style="width: 13%;">
                                จำนวนเงิน (ก่อน VAT)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, index) in details" :key="index">
                              <td class="text-center">{{ row.seqNo }}</td>
                              <td>{{ row.productTypeName }}</td>
                              <td>{{ row.productCode }}</td>
                              <td>{{ row.productName }}</td>
                              <td class="text-right">{{ formatQty(row.qty) }}</td>
                              <td class="text-center">{{ row.unitName }}</td>
                              <td class="text-right">{{ formatAmount(row.amount) }}</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr class="fss-total-row">
                              <!--
                                FS-036 ข้อ 2 — spec ฉบับเซ็นใช้คำว่า "Grand Total" (เดิมเป็น "รวมทั้งสิ้น")
                                คำเดียวกับแถบสรุปในฟอร์ม PDF และตารางขั้นที่ 2 โหมดหลายตัวแทน
                              -->
                              <td colspan="4" class="text-right kt-font-bold">Grand Total</td>
                              <td class="text-right kt-font-bold">{{ formatQty(totalQty) }}</td>
                              <td class="text-center">—</td>
                              <td class="text-right kt-font-bold">
                                {{ formatAmount(totalAmount) }}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <!--
                FS-116 — ไม่มีแถว Grand Total ชั้นนอก : มีตัวแทนแค่ 1 ราย ยอดซ้ำกับแถวตัวแทน
                  และ Grand Total ของตารางรายละเอียด (ผู้ใช้สั่ง 16/10/2026)
              -->
            </table>
          </div>
        </div>

        <!--
          FS-047 — ตัดกล่องสรุป (จำนวนสินค้ารวม / จำนวนเงินรวม / ตัวอักษร) ออกแล้ว
            เหตุผลเดียวกับหน้ารายละเอียด — ซ้ำกับแถว Grand Total ใน <tfoot>
            และ mockup ขั้นที่ 3 ของ spec จบที่แถว Grand Total แล้วต่อด้วยปุ่มทันที
          CSS .fss-summary* และ computed totalAmountText คงไว้ เผื่อกลับคำ
        -->
      </div>
      <!-- end: Summary table -->

      <!-- F04 (D10) บรรทัดสาขาย่อย — FS-074 ย้ายไปอยู่ใต้ชื่อตัวแทนในกล่อง "Filter ที่เลือก" แล้ว -->

      <!-- begin: FS-036 ข้อ 3 — บอกล่วงหน้าว่าพิมพ์/Export ได้ "หลังบันทึก" เท่านั้น -->
      <!--
        spec ฉบับเซ็น (mockup ขั้นที่ 2 "ตรวจสอบข้อมูล") มีบรรทัดนี้ใต้ตาราง
        ผู้ใช้จะได้ไม่มองหาปุ่มพิมพ์ PDF / Export Excel ในขั้นนี้ (ยังไม่มีเลขที่เอกสาร)
      -->
      <div
        v-if="!isNoData"
        class="fss-post-save-note"
        data-test="fss-preview-post-save-note"
      >
        <i class="la la-info-circle"></i>
        หลังบันทึกข้อมูลแล้ว จะสามารถพิมพ์ PDF หรือ Export Excel (รายละเอียดบาร์โค้ด) ได้
      </div>
      <!-- end: FS-036 ข้อ 3 -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: Actions -->
      <!-- ขั้นนี้ยังไม่มีเลขที่เอกสาร → ปุ่ม PDF/Excel/แนบไฟล์ ยังใช้ไม่ได้ (มีเฉพาะขั้นที่ 3) -->
      <div class="row">
        <!--
          FS-119 — คอลัมน์ซ้ายกว้างเท่าปุ่ม "แก้ไข Filter" · คอลัมน์ขวาได้ที่ที่เหลือทั้งหมด
            เดิม col-xl-6 / col-xl-6 : viewport 1400–1650px (เช่นจอ 1280 ที่ซูม 80/90%) ปุ่มขวาได้แค่ครึ่งจอ
            ไม่พอสำหรับปุ่มลายเซ็น + ปุ่มบันทึก → ปุ่มหดจนข้อความในปุ่มตกบรรทัด
        -->
        <div class="col-xl-auto">
          <button
            type="button"
            class="btn btn-sm btn-clean kt-margin-r-10"
            @click="goBackToFilter"
          >
            <i class="la la-arrow-left"></i>
            <span class="kt-hidden-mobile">แก้ไข Filter</span>
          </button>
        </div>
        <div class="col-xl text-right">
          <!--
            F15 — เลือกลายเซ็นก่อนบันทึก (เอกสารยังไม่มี Id จึงส่ง id = 0)
            FS-012 — แนบแล้ว = เขียว + เครื่องหมายถูก · ยังไม่แนบ = โทนจางเหมือนเดิม
          -->
          <!--
            FS-018 — spec (CR-Features.md:88 · matrix ข้อ 8) ระบุว่ากลุ่ม B (แอดมินตัวแทน)
            "ไม่แสดงปุ่ม" ระบบจะใช้ลายเซ็นค่าเริ่มต้นให้อัตโนมัติ
            ตัดสินด้วย canManage ที่ API ส่งมา ไม่เดาจาก role ฝั่ง client
            ค่าเริ่มต้นเป็น false = ซ่อนไว้ก่อน กันปุ่มแวบขึ้นมาแล้วหายตอน API ตอบ
          -->
          <!--
            FS-060 — thumbnail อยู่ "ในปุ่ม" ตาม markup ของ mockup
              <button style="display:flex; align-items:center; gap:8px">
                ✅ ลายเซ็นผู้จัดทำ (คลิกเพื่อเปลี่ยน)
                <span style="background:#fff; border:1px solid #145a34">Sig</span>
              </button>
            ของเดิมวางไว้นอกปุ่มเป็น element พี่น้องกัน ทำให้ตัดบรรทัดแยกกันได้เวลาจอแคบ
          -->
          <!--
            FS-067 — ปุ่มลายเซ็นกับปุ่มบันทึกต้องอยู่บรรทัดเดียวกันเสมอ
              ของเดิมเป็น inline element เรียงกันเฉย ๆ พอความกว้างรวมเกินคอลัมน์ (50%)
              ปุ่มหลังจะตกบรรทัด ซึ่งเกิดจริงที่ซูม 80% และ 90% แต่ไม่เกิดที่ 70% / 100%
              (ปุ่มลายเซ็นกว้างกว่าปกติเพราะมี thumbnail + ป้าย scope อยู่ในตัว — FS-060/FS-061)
              ครอบด้วย inline-flex + nowrap จึงตัดบรรทัดไม่ได้อีก · ระยะห่างยังใช้ margin เดิม
          -->
          <div class="fss-action-group">
          <button
            v-if="canManageSignature"
            type="button"
            class="btn btn-sm kt-margin-r-10 fss-signature-btn"
            :class="hasSignature ? 'btn-success' : 'btn-clean'"
            data-test="fss-open-signature"
            @click="openSignature"
          >
            <i :class="hasSignature ? 'la la-check' : 'la la-pencil'"></i>
            <span class="kt-hidden-mobile">{{ signatureButtonText }}</span>
            <img
              v-if="signatureImageUrl"
              :src="signatureImageUrl"
              class="fss-signature-thumb"
              alt="ลายเซ็นผู้จัดทำ"
              data-test="fss-signature-thumb"
            />
          </button>

          <button
            type="button"
            class="btn btn-sm btn-primary kt-margin-r-10"
            :disabled="isNoData"
            @click="saveDocument"
          >
            <i class="la la-save"></i>
            <span class="kt-hidden-mobile">บันทึกข้อมูล</span>
          </button>
          </div>
        </div>
      </div>
      <!-- end: Actions -->

      <!--
        FS-069 — @changed มาทุกครั้งที่บันทึกภาพสำเร็จ (ทั้งสองแถบ)
          ของเดิมไม่มีใครฟัง → บันทึกค่าเริ่มต้นระบบในแถบ 2 แล้ว thumbnail บนปุ่มค้างภาพเก่า
          loadDefaultSignature() มีด่าน "ถ้ามีภาพเฉพาะกิจอยู่แล้ว ไม่ทับ" อยู่ในตัว
          จึงเรียกได้เลย — ภาพเฉพาะกิจจากแถบ 1 ยังชนะค่าเริ่มต้นเสมอ
      -->
      <FormSummaryStockSignatureModal
        ref="signatureModal"
        @selected="onSignatureSelected"
        @changed="loadDefaultSignature"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
// Action Button Design System + Stepper ของโมดูลนี้ (QA เรื่องที่ 11 + 16)
import "./form-summary-stock-actions.css";
// FS-088 — tooltip ของปุ่มวงกลมเป็นแบบ Bootstrap (ผูกครั้งเดียวทั้งโมดูล เรียกซ้ำได้)
import { installFssTooltips } from "./form-summary-stock-tooltips";
installFssTooltips();
import router from "../../router";
import FormSummaryStockSignatureModal from "./FormSummaryStockSignatureModal";

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F04 — ขั้นที่ 2 "ตรวจสอบข้อมูล" (/PrintSummary/Barcodes/New/Preview)
//
// หน้านี้ยังไม่บันทึกอะไรลงฐานข้อมูล — แสดงผลลัพธ์ที่ FormSummaryStockCreate.vue
// ยิง /Api/FormSummaryStock/Process มาแล้ว เพื่อให้ผู้ใช้ตรวจก่อนกด "บันทึกข้อมูล"
//
// การรับ state จากขั้นที่ 1: อ่านจาก $parent (= FormSummaryStockCreate.vue ซึ่งเป็น route แม่)
//   ต้นแบบใน repo: SBP.Web/App/Components/BeerClaim/AddBeerClaimItem.vue:1113-1115
//                  (route ลูกอ่าน self.$parent.<dataProp> โดย route ส่งแค่ scalar)
//   repo นี้ไม่มี Vuex / store module / การส่ง object ผ่าน route params — $parent คือ pattern เดียวที่มี
//
// เข้าหน้านี้ตรง ๆ (refresh / กด URL) จะไม่มี $parent.processResult → เด้งกลับขั้นที่ 1
//
// SweetAlert2 = ^8.2.3 (SBP.Web/package.json) → ใช้ option ชื่อ `type` (ไม่ใช่ `icon` ของ v9+)
// และอ่านผลจาก `result.value`
// ─────────────────────────────────────────────────────────────────────────────

export default {
  components: { FormSummaryStockSignatureModal },
  data: function() {
    return {
      // F04 (D10) — บรรทัด "*สำนักงานใหญ่ + สาขา [ ... ]" ค่าว่าง = ไม่ต้องแสดง
      branchLine: "",

      // F15 — ภาพลายเซ็นเฉพาะกิจที่เลือกไว้ก่อนกดบันทึก
      //   เอกสารยังไม่มี Id ตอนนี้ จึงผูกกับ header ไม่ได้ — ถือ AttachedId ไว้ที่หน้านี้
      //   แล้วแนบไปกับคำสั่งบันทึก (null = ใช้ค่าเริ่มต้นของระบบ)
      signatureAttachedId: null,
      // FS-017 — ระบบมีลายเซ็นค่าเริ่มต้นอยู่หรือไม่ (ใช้ตัดสินสีปุ่มตอนยังไม่เลือกภาพเอง)
      hasDefaultSignature: false,
      // FS-017 — url ของภาพที่จะถูกใช้จริง สำหรับ thumbnail ข้างปุ่ม (signed URL อายุสั้น)
      signatureImageUrl: "",
      // FS-018 — สิทธิ์จัดการลายเซ็น (canManage จาก /Api/FormSummaryStock/Signature)
      //   ค่าเริ่มต้นต้องเป็น false เสมอ — ซ่อนปุ่มไว้ก่อนจนกว่า API จะยืนยันว่ามีสิทธิ์
      canManageSignature: false,
    };
  },
  computed: {
    // FS-012 — สถานะปุ่มลายเซ็น
    //   ขั้นนี้ยังไม่บันทึกเอกสาร "แนบแล้ว" จึงหมายถึงผู้ใช้เลือกภาพเฉพาะกิจไว้แล้ว
    //   (null = เอกสารฉบับนี้จะใช้ลายเซ็นค่าเริ่มต้นของระบบ)
    // FS-017 — "มีลายเซ็น" = เอกสารนี้จะพิมพ์ออกมาพร้อมภาพแน่นอน
    //   ไม่ได้เลือกภาพเฉพาะกิจก็ยังได้ลายเซ็นค่าเริ่มต้นของระบบ ซึ่ง spec (CR-Features.md:320)
    //   ระบุว่า "ระบบต้องมีภาพเสมอ" → สถานะเริ่มต้นจึงต้องเป็นเขียว ไม่ใช่โทนจาง
    //   (ของเดิมดูแค่ signatureAttachedId จึงจางตลอดจนกว่าจะเลือกเอง = ไม่ตรง design)
    hasSignature: function() {
      return !!this.signatureAttachedId || this.hasDefaultSignature;
    },
    signatureButtonText: function() {
      return this.hasSignature
        ? "ลายเซ็นผู้จัดทำ (คลิกเพื่อเปลี่ยน)"
        : "ลายเซ็นผู้จัดทำ";
    },

    // ผลลัพธ์จาก /Api/FormSummaryStock/Process ที่ขั้นที่ 1 เก็บไว้
    processResult: function() {
      return this.$parent && this.$parent.processResult
        ? this.$parent.processResult
        : null;
    },
    // ข้อความสรุป filter ภาษาไทยจาก API (ถ้าไม่มีจะ fallback ไปใช้ filterLabels ที่ประกอบฝั่ง client)
    filterText: function() {
      return this.processResult && this.processResult.filterText
        ? this.processResult.filterText
        : "";
    },
    filterLabels: function() {
      return this.$parent && this.$parent.filterLabels
        ? this.$parent.filterLabels
        : [];
    },
    details: function() {
      return this.processResult && this.processResult.details
        ? this.processResult.details
        : [];
    },
    totalQty: function() {
      return this.processResult ? this.processResult.totalQty : 0;
    },
    totalAmount: function() {
      return this.processResult ? this.processResult.totalAmount : 0;
    },
    totalAmountText: function() {
      return this.processResult ? this.processResult.totalAmountText : "";
    },
    // ไม่มีรายการให้บันทึก — ครอบทั้งกรณี status "ไม่ใช่สำนักงานใหญ่" และกรณีไม่มีบาร์โค้ดคงค้าง
    isNoData: function() {
      return this.details.length === 0;
    },

    // FS-108 — รหัสที่เลือก : ตัวแทนจำหน่าย = agentCode · Group = headAgentCode (filter ของขั้นที่ 1)
    selectedCode: function() {
      const filter = this.$parent ? this.$parent.filter : null;
      if (!filter) return "";
      return filter.filterType === "GROUP"
        ? filter.headAgentCode || ""
        : filter.agentCode || "";
    },
    selectedName: function() {
      return this.processResult && this.processResult.agentName
        ? this.processResult.agentName
        : "";
    },
    // "[รหัส] ชื่อ" แบบเดียวกับโหมดหลายตัวแทน (FS-082)
    agentLabel: function() {
      const code = this.selectedCode;
      const name = this.selectedName;
      if (!code) return name;
      return name ? "[" + code + "] " + name : "[" + code + "]";
    },
    // FS-106 ข้อ 2 / FS-107 — หัวรายละเอียดแสดงชื่ออย่างเดียว ไม่มี [รหัส]
    agentNameOnly: function() {
      return this.selectedName || this.selectedCode;
    },
    noDataMessage: function() {
      if (this.processResult && this.processResult.message) {
        return this.processResult.message;
      }
      return "ไม่พบรายการสินค้าคงค้างตามเงื่อนไขที่เลือก กรุณาแก้ไข Filter แล้วประมวลผลใหม่อีกครั้ง";
    },
  },
  methods: {
    formatQty: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US");
    },
    formatAmount: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    goBackToFilter: function() {
      router.push({ path: "/PrintSummary/Barcodes/New" });
    },

    // ── F04 (D10) : บรรทัดสาขาย่อย ────────────────────────────────────────
    //
    //   เอกสารยังไม่ถูกบันทึกในขั้นนี้ จึงส่ง filter ไปแทน headerId
    //   endpoint เดียวกับที่ฟอร์ม PDF ใช้ — ตัวเลขบนจอกับบนกระดาษจะได้ไม่เพี้ยนกัน
    //
    //   โหลดไม่สำเร็จก็ปล่อยให้บรรทัดนี้ว่างไว้ ไม่ต้องขึ้น error
    //   (เป็นข้อมูลประกอบ ไม่ใช่ตัวเลขที่ผู้ใช้ต้องตรวจ)
    loadBranchLine: function() {
      const self = this;
      const filter = self.$parent ? self.$parent.filter : null;

      if (!filter) return;

      axios
        .post(
          "/Api/FormSummaryStock/CustomerBox",
          {
            headerId: 0,
            filterType: filter.filterType,
            agentCode: filter.agentCode,
            headAgentCode: filter.headAgentCode,
          },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};
          self.branchLine = data.success && data.hasBranchLine ? data.branchLine : "";
        })
        .catch(function() {
          self.branchLine = "";
        });
    },

    // ── F15 : ลายเซ็นผู้จัดทำ ─────────────────────────────────────────────
    openSignature: function() {
      // ยังไม่มีเลขที่เอกสารในขั้นนี้ — ส่ง id = 0 ให้ popup รู้ว่าเป็นโหมดก่อนบันทึก
      // FS-070 — ส่งภาพที่หน้านี้ถืออยู่ไปด้วย ไม่งั้นเปิดครั้งที่ 2 popup จะลืมภาพที่เพิ่งบันทึก
      //   (โหมดก่อนบันทึก server ยังไม่มีเอกสารให้ผูกภาพ — หน้านี้คือที่เดียวที่รู้)
      this.$refs.signatureModal.open({
        id: 0,
        documentNo: "",
        attachedId: this.signatureAttachedId,
      });
    },

    onSignatureSelected: function(attachedId, imageUrl) {
      this.signatureAttachedId = attachedId || null;
      // FS-017 — ล้างภาพเฉพาะกิจแล้ว popup จะส่ง url ของค่าเริ่มต้นกลับมาแทน
      if (typeof imageUrl === "string") this.signatureImageUrl = imageUrl;
    },

    // FS-017 — อ่านลายเซ็นค่าเริ่มต้นของระบบตั้งแต่เปิดหน้า เพื่อให้ปุ่ม + thumbnail ถูกต้อง
    //   ใช้ endpoint เดียวกับ popup (headerId = 0 คือโหมดก่อนบันทึก) เป็น best-effort
    //   ล้มเหลวก็แค่ปุ่มกลับไปเป็นโทนจาง ไม่ต้องรบกวนผู้ใช้
    loadDefaultSignature: function() {
      const self = this;

      axios
        .post(
          "/Api/FormSummaryStock/Signature",
          { headerId: 0 },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};

          // FS-018 — handler เซ็ต CanManage ไว้ตั้งแต่บรรทัดแรกของ Handle() จึงเชื่อถือได้
          //   แม้ success = false — อ่านก่อนด่าน success กันกรณี storage ล่มแล้วกลุ่ม A เสียปุ่มไปด้วย
          self.canManageSignature = !!data.canManage;

          if (!data.success) return;

          self.hasDefaultSignature = !data.isMissingDefault && !!data.defaultAttachedId;
          if (!self.signatureAttachedId) {
            self.signatureImageUrl = data.documentImageUrl || data.defaultImageUrl || "";
          }
        })
        .catch(function() {
          self.hasDefaultSignature = false;
          // FS-018 — เรียกไม่สำเร็จ = ไม่รู้สิทธิ์ ให้ถือว่าไม่มีสิทธิ์ (ซ่อนปุ่มไว้ก่อน)
          self.canManageSignature = false;
        });
    },

    // แนบลายเซ็นเป็น query string — ไม่ปนกับ filter ที่ถูก serialize เก็บเป็น FilterJson
    buildSaveUrl: function() {
      const base = "/Api/FormSummaryStock/Save";
      return this.signatureAttachedId
        ? base + "?signatureAttachedId=" + encodeURIComponent(this.signatureAttachedId)
        : base;
    },
    saveDocument: function() {
      var self = this;

      // filter object ชุดเดียวกับที่ใช้ประมวลผล — บันทึกแล้ว snapshot ต้องตรงกับที่ผู้ใช้เพิ่งตรวจ
      var filter = self.$parent ? self.$parent.filter : null;
      if (!filter) {
        Swal.fire(
          "ไม่พบข้อมูล",
          "ไม่พบเงื่อนไขที่ใช้ประมวลผล กรุณากลับไปเลือก Filter แล้วประมวลผลใหม่อีกครั้ง",
          "warning"
        );
        return;
      }

      Swal.fire({
        title: "ยืนยันการบันทึกข้อมูล",
        type: "warning",
        html:
          "ต้องการบันทึกเอกสารสรุปสินค้าคงค้างใช่หรือไม่?<br/>" +
          "<small class='text-muted'>ระบบจะล็อค snapshot ของข้อมูล และออกเลขที่เอกสารให้อัตโนมัติ</small>",
        showCancelButton: true,
        confirmButtonText: "ยืนยันบันทึก",
        cancelButtonText: "ยกเลิก",
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          KTApp.blockPage({
            opacity: 0.05,
            message: "กำลังบันทึกข้อมูล กรุณารอสักครู่...",
          });

          axios
            .post(self.buildSaveUrl(), filter, {
              headers: {
                Authorization: "Bearer " + self.$root.user.access_token,
              },
            })
            .then(function(response) {
              if (response.data.success) {
                // รีเฟรชรายการใบงานที่หน้าค้นหา (route ปู่ = FormSummaryStockList.vue)
                // ต้นแบบเรียกข้ามสองชั้น: BeerClaim/AnalyzeSpecifyForm.vue:176
                if (
                  self.$parent &&
                  self.$parent.$parent &&
                  typeof self.$parent.$parent.reloadDataTable === "function"
                ) {
                  self.$parent.$parent.reloadDataTable();
                }

                Swal.fire("สำเร็จ", response.data.message || "บันทึกข้อมูลสำเร็จ", "success");

                // ไปขั้นที่ 3 = ใบงานที่บันทึกแล้ว
                router.push({
                  path: "/PrintSummary/Barcodes/" + response.data.documentNo,
                });
              } else {
                Swal.fire(
                  "เกิดข้อผิดพลาด",
                  response.data.message || "ไม่สามารถบันทึกข้อมูลได้",
                  "error"
                );
              }
            })
            .catch(function(error) {
              var errorMsg =
                error.response && error.response.data && error.response.data.message
                  ? error.response.data.message
                  : "เกิดข้อผิดพลาด";
              Swal.fire("เกิดข้อผิดพลาด", errorMsg, "error");
            })
            .then(function() {
              KTApp.unblockPage();
            });
        }
      });
    },
  },
  mounted() {
    this.loadBranchLine();
    this.loadDefaultSignature();
    // เข้าหน้านี้ตรง ๆ โดยไม่ผ่านขั้นที่ 1 (refresh / paste URL) → ไม่มีผลประมวลผลให้แสดง
    if (!this.processResult) {
      router.replace({ path: "/PrintSummary/Barcodes/New" });
    }
  },
};
</script>

<style lang="scss" scoped>

/*
  FS-045 — Metronic ตั้ง .alert เป็น display:flex (flex-wrap: nowrap)
  ลูกทุกตัวจึงกลายเป็น flex item แยกกัน ทั้ง <i>, <strong> และ text node
  ผลคือ <strong> ถูกบีบตามพื้นที่ที่เหลือ (วัดได้ 33.8px) แล้วตัดคำกลางคำ
  "ข้อสำคัญ" กลายเป็น "ข้อ" บรรทัดบน "สำคัญ" บรรทัดล่าง ทับกับข้อความถัดไป
  และช่องไฟระหว่างก้อนก็หายเพราะ flex ไม่มี white-space ระหว่าง item

  คืนให้ไหลเป็นข้อความปกติ — ไอคอนเป็น inline นำหน้าเหมือนที่ design ตั้งใจ
*/
.alert {
  display: block;
}

.alert > i {
  margin-right: 0.35rem;
}

/* FS-017 — thumbnail ลายเซ็นข้างปุ่ม (ตาม design) ภาพมาจาก signed URL ของ endpoint Signature
   ห้ามชี้ไปที่ storage.googleapis.com ตรง ๆ — bucket ปิด เบราว์เซอร์จะได้ 403 (เหตุผลเดียวกับ FS-015) */
/* FS-060 — ปุ่มลายเซ็นเป็น flex ตาม mockup (gap 8px) เพื่อให้ thumbnail อยู่ในปุ่ม */
/* FS-067 — ปุ่มลายเซ็น + ปุ่มบันทึก ต้องอยู่บรรทัดเดียวกันทุกระดับซูม
   inline-flex เพื่อให้กล่องยังถูกจัดชิดขวาโดย .text-right ของคอลัมน์
   nowrap คือหัวใจ — ห้ามตัดบรรทัดไม่ว่าความกว้างคอลัมน์จะเหลือเท่าไร
   ระยะห่างยังใช้ kt-margin-r-10 เดิมของปุ่ม จึงไม่ต้องใส่ gap ให้ซ้อนกัน */
.fss-action-group {
  display: inline-flex;
  flex-wrap: nowrap;
  /* FS-073 — stretch ให้ปุ่มบันทึกสูงเท่าปุ่มลายเซ็น (ซึ่งสูงกว่าเพราะมี thumbnail 28px อยู่ในปุ่ม)
     ข้อความใน <button> อยู่กึ่งกลางแนวตั้งเองอยู่แล้ว · ไม่ตั้งความสูงตายตัว thumbnail เปลี่ยนขนาดก็ตามไปเอง */
  align-items: stretch;
  justify-content: flex-end;
  max-width: 100%;
}

/* FS-119 — ปุ่มไม่หด และข้อความในปุ่มไม่ตัดบรรทัด (กันซ้ำถ้าคอลัมน์แคบกว่าที่คาด) */
.fss-action-group > .btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.fss-signature-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fss-signature-thumb {
  height: 24px;
  max-width: 96px;
  object-fit: contain;
  /* พื้นขาวบังคับ — อยู่ในปุ่มเขียวแล้ว ลายเซ็นสีเข้มจะจมถ้าไม่มีพื้น */
  background: #fff;
  /* FS-060 — กรอบเขียวเข้มตาม mockup (#145a34) ให้เข้ากับปุ่ม ไม่ใช่เทาแบบเดิม */
  border: 1px solid #145a34;
  border-radius: 3px;
  padding: 1px 3px;
  vertical-align: middle;
}

/* FS-074 — QA เรื่องที่ 1: ใต้ชื่อตัวแทน · ตัวเล็กกว่าชื่อหลัก (~11pt) · สีเทา */
.fss-branch-line {
  margin-top: 0.25rem;
  color: #74788d;
  font-size: 0.85rem;
  font-weight: normal;
}

/* FS-036 ข้อ 3 — บรรทัดแจ้งว่าพิมพ์/Export ได้หลังบันทึก
   โทนเดียวกับ .info-note ใน mockup ของ spec (แถบอ่อน + ขีดซ้าย) */
.fss-post-save-note {
  margin: 0.5rem 0 0.75rem;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #5d78ff;
  background-color: #f2f3f8;
  color: #6c7293;
  font-size: 0.9rem;
}

.fss-filter-text {
  padding: 0.5rem 0.75rem;
  background-color: #f7f8fa;
  border-radius: 4px;
}

.fss-filter-table {
  margin-bottom: 0;

  &__label {
    width: 12rem;
    font-weight: 600;
    color: #6c7293;
  }
}

.fss-total-row {
  background-color: #f7f8fa;
}

/* FS-108 — แถวรายละเอียดใต้แถวตัวแทน : ค่าเดียวกับ FormSummaryStockGroupPreview.vue */
.fss-detail-row > td {
  background-color: #fafbfc;
}

.fss-detail {
  padding: 0.5rem 0.25rem;

  &__title {
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #6c7293;
  }
}

.fss-summary {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background-color: #f7f8fa;
  border-radius: 4px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0.25rem 0;
  }

  &__label {
    color: #6c7293;
    margin-right: 1rem;
  }

  &__value {
    font-weight: 600;
    text-align: right;
  }
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockPreview.vue